import * as React from 'react';
import { Coords } from 'google-map-react';

import { Search } from '@weddingspot/ws-api-client';
import MapAnnotation from '../google-map/MapAnnotation';
import ReactDOM = require('react-dom');
import MapZoomControl from '../google-map/MapZoomControl';
import MapRedoSearchControl from '../google-map/MapRedoSearchControl';
import { useFormikContext } from 'formik';
import { SRPFormikValues } from './index';
import { ParsedQuery } from './hooks/ApiHookCreator';
import { padLatLng } from '../google-map/utils';
import Map, { MarkerProps } from '../google-map/MapWithMarkers';
import { MapContext } from '../google-map/MapContext';
import AppLogger from '../../utils/logging';
const logger = AppLogger.createLogger();

const styles = require('./MapView.scss');

interface VenueMapComponentProps {
    venues: Search.SearchResult[];
    venuesIsLoading: boolean;
    venuesCoordinates?: [number, number, number, number];
    search: ParsedQuery;
}

/**
 * This component is somewhat complicated because of the interactions between the underlying
 * GoogleMapReact component, the React component lifecycle, the Google Maps API, and our application state.
 *
 * Challenges are as follows:
 * - GoogleMapReact "loads" in two distinct phases:
 *     - component mount and internal initialization
 *     - Google Maps API load
 * - Map bounds/center/zoom state won't get updated properly unless the state/props change occurs after
 *   the API is loaded, we handle this by keeping track of the googleApiLoaded state and doing
 *   map bounds recomputation when that value changes
 * - We can't reasonably instruct GoogleMapReact to render the map without having the following:
 *     - a collection of venue locations to center the map around
 *     - the map's width/height after it's been mounted to the DOM tree
 * - We work around this by keeping track of these values as state and only mounting GoogleMapReact when
 *   mapReadyToMount = true
 */
export const VenueMapComponent: React.FC<VenueMapComponentProps> = (props: VenueMapComponentProps) => {
    const { venues, venuesIsLoading, search, venuesCoordinates } = props;
    const cleanedVenues = venues
        .filter((v) => !!v.coordinates)
        .map((v) => {
            const coord = v.coordinates as [number, number];
            return { ...v, lat: coord[0], lng: coord[1] };
        });

    // The venue that the annotation is showing for
    const [focusedVenueKey, setFocusedVenueKey] = React.useState<string | null>(null);

    // Width/height components for computing map bounds
    const [mapAltered, setMapAltered] = React.useState(false);
    const [mapCenter, setMapCenter] = React.useState<Coords>();
    const [mapZoom, setMapZoom] = React.useState<number>();

    const formikContext = useFormikContext<SRPFormikValues>();
    const { setFieldValue, setFieldTouched } = formikContext;

    // Logic for showing/hiding redo button
    const redoRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        if (redoRef.current) {
            // can't use display or hidden here cause it leads to control mispositioning ¯\_(ツ)_/¯
            redoRef.current.style.visibility = mapAltered ? 'visible' : 'hidden';
        }
    }, [redoRef.current, mapAltered]);

    const shouldOverrideBounds = (googleApiLoaded: boolean) => {
        return (!!search.bbox && googleApiLoaded) || !!venuesCoordinates;
    };

    const overrideBounds = (markerProps: MarkerProps[], googleApiLoaded: boolean) => {
        if (search.bbox && googleApiLoaded) {
            // if map bbox was used do not recalculate any map params
            return;
        } else if (venuesCoordinates) {
            // if page is just loaded and coordinates are provided
            // try to get bet fit to passed coordinates
            // NOTE: after page reload different map chunk could be loaded.
            // This is because if map bbox is exactly on edge between zoom levels and
            // fitBounds method will do one extra zoom out, while we don't need this.
            // to avoid this we're making bbox just a bit smaller so it can be fitted into wanted zoom level.
            const delta = 0.00000001;
            return {
                ne: {
                    lat: venuesCoordinates[0] - delta,
                    lng: venuesCoordinates[1] - delta,
                },
                sw: {
                    lat: venuesCoordinates[2] + delta,
                    lng: venuesCoordinates[3] + delta,
                },
            };
        } else {
            return;
        }
    };

    React.useEffect(() => {
        // For performance reason I decide directly manipulate of DOM element instead of using React flow.
        const globalClickListener = (e: MouseEvent) => {
            const el = e.target as Node;
            if (!el || !focusedVenueKey) {
                return;
            }

            const activePin = document.querySelector(`[data-annotation-id="${focusedVenueKey}"]`);
            const venueCard = document.querySelector(`.is-map [data-id="${focusedVenueKey}"]`);

            const pinClicked = activePin && activePin.contains(el);
            const venueClicked = venueCard && venueCard.contains(el);

            if (!pinClicked && !venueClicked) {
                // If we have selected venue, and user click somewhere else unselect it.
                setFocusedVenueKey(null);
                document.querySelector('.is-map .srp-venues-container')?.classList.remove('have-selected-venue');
            }
        };
        document.addEventListener('click', globalClickListener);
        return () => document.removeEventListener('click', globalClickListener);
    }, [focusedVenueKey]);

    // Handler for when a map marker is clicked
    const handleAnnotationClick = (hoverKey: string, childProps: MarkerProps) => {
        const venueCard = document.querySelector(`.is-map [data-id="${hoverKey}"]`);

        if (venueCard) {
            document.querySelector('.is-map .srp-venues-container')?.classList.add('have-selected-venue');
            venueCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
            document.querySelectorAll('.is-selected-venue').forEach((item) => item.classList.remove('is-selected-venue'));
            venueCard.classList.add('is-selected-venue');
        }
        setFocusedVenueKey(hoverKey);

        setMapCenter({ lat: childProps.lat, lng: childProps.lng });
    };

    // When venues are loading, unfocus the current venue
    React.useEffect(() => {
        if (props.venuesIsLoading) {
            setFocusedVenueKey(null);
        }
    }, [props.venuesIsLoading]);

    const redoSearch = (map: google.maps.Map) => {
        const bbox = map.getBounds();
        logger.info(`redoSearch bbox: ${JSON.stringify(bbox)}`);
        setTimeout(() => {
            if (!!bbox) {
                // Apply margin and generate new bounds
                const paddedBbox = padLatLng(bbox, 0);

                setFieldValue('bbox', paddedBbox.getNorthEast().toUrlValue(9) + ',' + paddedBbox.getSouthWest().toUrlValue(9));
                setFieldTouched('bbox', true);
            }
        });
        setMapAltered(false);
    };

    const handleOnLoad = (map: google.maps.Map) => {
        // place custom controls on google map
        const controls: [JSX.Element, google.maps.ControlPosition][] = [
            [
                // 9 is the middle value
                <MapZoomControl key='MapZoom' onChange={(incr: 1 | -1) => map.setZoom((map.getZoom() || 9) + incr)} />,
                google.maps.ControlPosition.LEFT_TOP,
            ],
            [
                <div key='MapRedo' ref={redoRef} style={{ visibility: 'hidden' }}>
                    <MapRedoSearchControl onClick={() => redoSearch(map)} />
                </div>,
                google.maps.ControlPosition.TOP_CENTER,
            ],
        ];

        controls.map(([element, position]) => {
            const controlDiv = document.createElement('div');
            ReactDOM.render(element, controlDiv);
            map.controls[position].push(controlDiv);
        });
    };

    const renderMarker = (props: MarkerProps) => {
        return (
            <MapAnnotation
                key={`${props.id}`} // Has to be a string for some reason
                lat={props.lat}
                lng={props.lng}
                focusedVenueKey={focusedVenueKey}
                venueId={props.id as number}
            />
        );
    };

    return (
        <MapContext.Provider
            value={{
                mapCenter: mapCenter,
                mapZoom: mapZoom,
                setMapCenter: setMapCenter,
                setMapZoom: setMapZoom,
            }}
        >
            <Map
                isLoading={venuesIsLoading}
                markerProps={cleanedVenues}
                onMarkerClick={handleAnnotationClick}
                onMarkersFit={() => setMapAltered(false)}
                renderMarker={renderMarker}
                overrideBounds={overrideBounds}
                shouldOverrideBounds={shouldOverrideBounds}
                onGoogleMapLoad={handleOnLoad}
                googleMapsProps={{
                    onDragEnd: () => setMapAltered(true),
                    onZoomAnimationEnd: () => setMapAltered(true),
                    options: {
                        zoomControl: false,
                        fullscreenControl: false,
                    },
                }}
            />
        </MapContext.Provider>
    );
};

VenueMapComponent.displayName = 'VenueMapComponent';
